import { cn } from '@sohanemon/utils';
import {
	SurfaceFrame,
	SurfaceResizeHandles,
	useSurfaceDrag,
	WindowChrome,
} from '@sohanscript/ui/dialog';
import { animate } from 'motion/react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsFocused, useWindowControl } from '@/lib/store/window-manager';

type InitialPosition = {
	left?: number;
	right?: number;
	top?: number;
	bottom?: number;
};

type WindowProps = {
	id: string;
	children: React.ReactNode;
	contentClassName?: string;
	initialPosition?: InitialPosition;
	maximizedMode?: 'cover' | 'dock';
};

const DEFAULT_MIN_WIDTH = 350;
const DEFAULT_MIN_HEIGHT = 450;

export function Window({
	id,
	children,
	contentClassName,
	initialPosition,
	maximizedMode = 'dock',
}: WindowProps) {
	const {
		win,
		close,
		minimize,
		toggleMaximize,
		bringToFront,
		resize,
		clearShake,
	} = useWindowControl(id);
	const isFocused = useIsFocused(id);
	const [isResizing, setIsResizing] = useState(false);

	const isShaking = win?.isShaking ?? false;
	const maximized = win?.status === 'maximized';
	const minimized = win?.status === 'minimized';

	const minWidth = win?.minWidth ?? DEFAULT_MIN_WIDTH;
	const minHeight = win?.minHeight ?? DEFAULT_MIN_HEIGHT;

	const initialOffset = useMemo(
		() => ({
			x:
				initialPosition?.left !== undefined
					? -(initialPosition.left ?? 0)
					: (initialPosition?.right ?? 0),
			y:
				initialPosition?.top !== undefined
					? -(initialPosition.top ?? 0)
					: (initialPosition?.bottom ?? 0),
		}),
		[initialPosition],
	);

	const drag = useSurfaceDrag({
		enabled: !maximized && !minimized && !isResizing,
		placement: 'origin',
		initialOffset,
	});

	useEffect(() => {
		if (!isShaking) return;

		const currentX = drag.x.get();
		animate(
			drag.x,
			[
				currentX,
				currentX - 8,
				currentX + 8,
				currentX - 6,
				currentX + 6,
				currentX - 3,
				currentX + 3,
				currentX,
			],
			{
				duration: 0.5,
				ease: 'easeInOut',
			},
		);
		const shakeTimeout = setTimeout(clearShake, 500);
		return () => clearTimeout(shakeTimeout);
	}, [isShaking, clearShake, drag.x]);

	const handleResize = useCallback(
		(size: { width: number; height: number }) => {
			resize(size.width, size.height);
		},
		[resize],
	);

	useEffect(() => {
		// NOTE: only the focused window answers the escape key, the desktop
		// can hold several windows at once.
		if (!isFocused) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			const target = event.target;
			if (
				target instanceof HTMLElement &&
				(target.isContentEditable ||
					['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
			) {
				return;
			}
			event.preventDefault();
			close();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [close, isFocused]);

	if (!win || win.status === 'closed') return null;

	return (
		<SurfaceFrame
			variant="window"
			placement="origin"
			maximized={maximized}
			maximizedMode={maximizedMode}
			isFocused={isFocused}
			draggable={!maximized && !minimized && !isResizing}
			dragController={drag}
			state={minimized ? 'minimized' : 'open'}
			style={{
				zIndex: win.zIndex,
				width: maximized ? undefined : win.width,
				height: maximized ? undefined : win.height,
				minWidth,
				minHeight,
				pointerEvents: minimized ? 'none' : 'auto',
			}}
			onPointerDown={() => bringToFront()}
		>
			<SurfaceResizeHandles
				onResize={handleResize}
				minWidth={minWidth}
				minHeight={minHeight}
				frameRef={drag.frameRef}
				x={drag.x}
				y={drag.y}
				disabled={maximized || minimized}
				onResizingChange={setIsResizing}
			/>

			<WindowChrome
				label={win.title}
				onClose={close}
				onMinimize={minimize}
				onMaximize={toggleMaximize}
				isMaximized={maximized}
			/>

			<div className={cn('flex-1 overflow-auto', contentClassName)}>
				{children}
			</div>
		</SurfaceFrame>
	);
}
